// Pipeline board — read-model client for the single, global, runtime-derived
// board. Distinct from api/native.ts: /board remains the editable backlog,
// while this API projects every root pipeline (and not-yet-launched native
// task) into five fixed, non-draggable lanes. Card position is derived from
// server state only — moves are explicit button-driven ready-state writes.

import { apiRequest } from "./client";
import type { ExternalLinkInput, NativeIssue } from "./native";

const BASE = "/api/v1/pipeline-board";

// The five fixed lanes, in the order the server emits them.
export interface PipelineBoardColumn {
  id: string; // "opened" | "in_progress" | "closed"
  title: string;
  kind: string;
}

// One paused human interaction somewhere in a root's tree (the root itself or
// any descendant). The IN_PROGRESS card presents these one at a time; each
// answer targets the exact run_id shown here.
export interface PipelineBoardPendingReview {
  run_id: string;
  workflow_name?: string;
  bot_id?: string;
  node_id?: string;
  interaction_id?: string;
  questions?: Record<string, unknown>;
  /**
   * Resolved `instructions:` prompt of the paused human node — the author's
   * operator-facing question. `questions` above is the node's INBOUND data,
   * which the schema-driven form does not render (it renders the node's
   * OUTPUT fields), so for a bot that puts its whole question in
   * `instructions:` this is the only readable content on the card.
   */
  instructions?: string;
  /** When this exact pending turn joined the FIFO review queue. */
  updated_at: string;
  depth: number;
  /**
   * Stable identifier for sibling human gates launched by the same fan-out.
   * Gates carrying the same key can be filled one page at a time and sent
   * together. Absent for ordinary, unrelated pauses.
   */
  batch_key?: string;
}

// One dispatcher attempt associated with a native task-backed root.
export interface PipelineBoardAttempt {
  run_id?: string;
  status?: string;
  at?: string;
}

// Resolved hard dependency on another ticket (server-side enrichment).
export interface PipelineBoardBlocker {
  id: string;
  title?: string;
  state?: string;
  bot?: string;
  labels?: string[];
  satisfied: boolean;
  missing_labels?: string[];
}

// Reverse-index entry: a ticket that lists this card as a blocker.
export interface PipelineBoardBlocking {
  id: string;
  title?: string;
}

/**
 * The dispatcher's give-up stamp: this ticket's terminal state was written by
 * an exhausted retry budget, not by a human filing it. The two are the same
 * state on the board, so this is the only thing that tells them apart.
 */
export interface PipelineBoardGiveUp {
  /** The run whose failure exhausted the budget. */
  run_id: string;
  /** The state the give-up wrote (usually the board's `blocked`). */
  state?: string;
  /** How many attempts were burned before giving up. */
  attempts?: number;
  /**
   * Why the dispatcher gave up when it was not a retry budget — the claim
   * watchdog filing a card whose recorded run is gone. Empty for a
   * retry-budget give-up, which reads by `attempts`.
   */
  reason?: string;
  /** When the give-up happened (RFC 3339). */
  at?: string;
}

/** Spawned child ticket under a planner parent. */
export interface PipelineBoardChildRef {
  issue_id: string;
  title?: string;
  state?: string;
  bot_id?: string;
  card_id?: string;
}

/** Aggregated child statuses for a plan group face. */
export interface PipelineBoardChildrenSummary {
  total: number;
  ready: number;
  in_progress: number;
  done: number;
  failed: number;
  open: number;
}

// PipelineBoardCard is the read model the studio polls: one per root pipeline
// (or per not-yet-launched native task). Descendants are folded into their
// root — there are no per-child cards.
export interface PipelineBoardCard {
  id: string;
  kind: "task" | "run" | string;
  column_id: string;
  title: string;
  body?: string;

  // Native task provenance (present when the root is backed by a board issue).
  issue_id?: string;
  issue_state?: string;
  labels?: string[];
  priority?: number;

  // Hard-dependency graph (ticket roots). open_blocker_count > 0 means the
  // launch loop will refuse even if ready is true.
  blockers?: PipelineBoardBlocker[];
  open_blocker_count?: number;
  launch_blocked_reason?: string;
  blocking?: PipelineBoardBlocking[];

  // Planner provenance (distinct from hard blockers).
  parent_issue_id?: string;
  parent_title?: string;
  children?: PipelineBoardChildRef[];
  children_summary?: PipelineBoardChildrenSummary;
  /** planner | producer when known. */
  role?: string;

  // Run identity (empty for a not-yet-launched task card).
  run_id?: string;
  workflow_name?: string;
  bot_id?: string;
  status?: string;
  error?: string;

  // The run failed or was cancelled, as opposed to finishing successfully.
  // Spans two lanes: `needs_attention` (died mid-flight) and `closed`
  // (stopped by the operator). Retry stages the ticket back to Ready.
  failed?: boolean;
  // A `needs_attention` card holding one of the local concurrency slots open
  // for its own restart. Nothing is running against it — retrying, resuming
  // or closing the card releases the slot.
  reserves_slot?: boolean;
  // Set when the ticket's TERMINAL state was written by the dispatcher giving
  // up (its retry budget ran out), not by an operator filing it. It is why
  // such a card sits in Needs attention while its ticket reads "blocked".
  gave_up?: PipelineBoardGiveUp;
  // Ready lane (wire ID "opened") — a task-backed ticket staged (waiting for a
  // concurrency slot; the studio auto-launches it when one frees).
  ready?: boolean;

  // Ready lane — launch vars / task bot-args, and the concurrency-queue place.
  entry_input?: Record<string, unknown>;
  queue_position?: number;

  // IN_PROGRESS lane — node progress for the root and the whole tree.
  executed_nodes: number;
  total_nodes: number;
  tree_executed_nodes: number;
  tree_total_nodes: number;
  descendant_count?: number;
  // The whole run tree (root first, then descendants). The sidebar fans out
  // over these to aggregate every sub-bot's produced elements onto the card.
  tree_run_ids?: string[];
  pending_reviews?: PipelineBoardPendingReview[];

  // DONE lane — the pipeline's output (final_answer, else latest artifact).
  output?: string;

  // Forge/repo identity: task-backed cards get the issue's linkage
  // (provider + connection_id + repo_full_name); run-only cards fall back to
  // `{repo: <project_path>}` with empty provider/connection_id — treat `repo`
  // as the identity key for filtering in either case.
  external?: ExternalLinkInput;

  attempts?: PipelineBoardAttempt[];
  created_at: string;
  updated_at: string;
}

// The local pipeline-concurrency gate. When `enabled` is false the other
// fields are zero and the Ready lane only holds not-yet-launched native tasks.
export interface PipelineConcurrency {
  enabled: boolean;
  max: number;
  active: number;
  waiting: number;
  // Slots held by needs-attention pipelines so they can restart into them.
  // Absent from older servers, hence optional — treat as 0.
  reserved?: number;
}

// PipelineBoard is the aggregate global read model returned by the board GET.
export interface PipelineBoard {
  columns: PipelineBoardColumn[];
  cards: PipelineBoardCard[];
  concurrency: PipelineConcurrency;
  generated_at?: string;
  topology_error?: string;
}

export interface CreatePipelineTaskInput {
  // The bot the created task runs as. Required — the board is global, so the
  // bot comes from the request body (not a URL path).
  bot: string;
  title: string;
  body?: string;
  labels?: string[];
  priority?: number;
  bot_args?: Record<string, string>;
  /** Hard deps: issue IDs that must reach done before this ticket can launch. */
  blockers?: string[];
  start?: boolean;
  // Optional repo-first scoping: link the new task to a connected forge repo
  // at creation. Mirrors NativeIssueCreate.external.
  external?: ExternalLinkInput;
  /** Reconcile by (bot, bot_args.input_path) instead of creating a duplicate. */
  upsert?: boolean;
}

type UnknownRecord = Record<string, unknown>;

function record(value: unknown): UnknownRecord | null {
  return value !== null && typeof value === "object" && !Array.isArray(value)
    ? (value as UnknownRecord)
    : null;
}

function text(value: unknown): string | undefined {
  return typeof value === "string" && value.trim() ? value : undefined;
}

function numberValue(value: unknown): number | undefined {
  return typeof value === "number" && Number.isFinite(value) ? value : undefined;
}

function intValue(value: unknown, fallback = 0): number {
  const n = numberValue(value);
  return n === undefined ? fallback : Math.trunc(n);
}

function booleanValue(value: unknown): boolean {
  return value === true;
}

function stringArray(value: unknown): string[] | undefined {
  if (!Array.isArray(value)) return undefined;
  return value.filter((entry): entry is string => typeof entry === "string");
}

function normalizeAttempts(value: unknown): PipelineBoardAttempt[] | undefined {
  if (!Array.isArray(value)) return undefined;
  return value.map((entry) => {
    const source = record(entry) ?? {};
    return {
      ...(text(source.run_id) ? { run_id: text(source.run_id) } : {}),
      ...(text(source.status) ? { status: text(source.status) } : {}),
      ...(text(source.at) ? { at: text(source.at) } : {}),
    };
  });
}

function normalizeGiveUp(value: unknown): PipelineBoardGiveUp | undefined {
  const source = record(value);
  if (!source) return undefined;
  const run_id = text(source.run_id);
  // A stamp with no run cannot be attributed to the card showing it, and the
  // server never emits one — treat it as absent rather than render a claim
  // about an unknown run.
  if (!run_id) return undefined;
  const out: PipelineBoardGiveUp = { run_id };
  const state = text(source.state);
  if (state) out.state = state;
  const attempts = numberValue(source.attempts);
  if (attempts !== undefined) out.attempts = attempts;
  const reason = text(source.reason);
  if (reason) out.reason = reason;
  const at = text(source.at);
  if (at) out.at = at;
  return out;
}

// normalizeExternal tolerates the two shapes the server emits: a task-backed
// card's full `{provider, connection_id, repo, ...}` link, and a run-only
// fallback that carries only `repo` (project_path; provider/connection_id
// empty strings on the wire). Anything missing `repo` is treated as absent.
function normalizeExternal(value: unknown): ExternalLinkInput | undefined {
  const source = record(value);
  if (!source) return undefined;
  const repo = text(source.repo);
  if (!repo) return undefined;
  const out: ExternalLinkInput = {
    provider: text(source.provider) ?? "",
    connection_id: text(source.connection_id) ?? "",
    repo,
  };
  const number = numberValue(source.number);
  if (number !== undefined) out.number = number;
  const url = text(source.url);
  if (url) out.url = url;
  const state = text(source.state);
  if (state) out.state = state;
  return out;
}

function normalizeBlockers(value: unknown): PipelineBoardBlocker[] | undefined {
  if (!Array.isArray(value)) return undefined;
  return value
    .map((entry) => {
      const source = record(entry) ?? {};
      const id = text(source.id);
      if (!id) return null;
      return {
        id,
        ...(text(source.title) ? { title: text(source.title) } : {}),
        ...(text(source.state) ? { state: text(source.state) } : {}),
        ...(text(source.bot) ? { bot: text(source.bot) } : {}),
        ...(stringArray(source.labels) !== undefined
          ? { labels: stringArray(source.labels) }
          : {}),
        satisfied: booleanValue(source.satisfied),
        ...(stringArray(source.missing_labels) !== undefined
          ? { missing_labels: stringArray(source.missing_labels) }
          : {}),
      };
    })
    .filter((b): b is PipelineBoardBlocker => b !== null);
}

function normalizeBlocking(value: unknown): PipelineBoardBlocking[] | undefined {
  if (!Array.isArray(value)) return undefined;
  return value
    .map((entry) => {
      const source = record(entry) ?? {};
      const id = text(source.id);
      if (!id) return null;
      return {
        id,
        ...(text(source.title) ? { title: text(source.title) } : {}),
      };
    })
    .filter((b): b is PipelineBoardBlocking => b !== null);
}

function normalizeChildRefs(
  value: unknown,
): PipelineBoardChildRef[] | undefined {
  if (!Array.isArray(value)) return undefined;
  return value
    .map((entry) => {
      const source = record(entry) ?? {};
      const issueID = text(source.issue_id);
      if (!issueID) return null;
      return {
        issue_id: issueID,
        ...(text(source.title) ? { title: text(source.title) } : {}),
        ...(text(source.state) ? { state: text(source.state) } : {}),
        ...(text(source.bot_id) ? { bot_id: text(source.bot_id) } : {}),
        ...(text(source.card_id) ? { card_id: text(source.card_id) } : {}),
      };
    })
    .filter((c): c is PipelineBoardChildRef => c !== null);
}

function normalizeChildrenSummary(
  value: unknown,
): PipelineBoardChildrenSummary | undefined {
  const source = record(value);
  if (!source) return undefined;
  return {
    total: intValue(source.total, 0),
    ready: intValue(source.ready, 0),
    in_progress: intValue(source.in_progress, 0),
    done: intValue(source.done, 0),
    failed: intValue(source.failed, 0),
    open: intValue(source.open, 0),
  };
}

function normalizePendingReviews(
  value: unknown,
): PipelineBoardPendingReview[] | undefined {
  if (!Array.isArray(value)) return undefined;
  return value.map((entry) => {
    const source = record(entry) ?? {};
    const questions = record(source.questions) ?? undefined;
    return {
      run_id: text(source.run_id) ?? "",
      ...(text(source.workflow_name)
        ? { workflow_name: text(source.workflow_name) }
        : {}),
      ...(text(source.bot_id) ? { bot_id: text(source.bot_id) } : {}),
      ...(text(source.node_id) ? { node_id: text(source.node_id) } : {}),
      ...(text(source.interaction_id)
        ? { interaction_id: text(source.interaction_id) }
        : {}),
      ...(questions ? { questions } : {}),
      ...(text(source.instructions)
        ? { instructions: text(source.instructions) }
        : {}),
      ...(text(source.batch_key) ? { batch_key: text(source.batch_key) } : {}),
      updated_at: text(source.updated_at) ?? "",
      depth: Math.max(0, intValue(source.depth, 0)),
    };
  });
}

export function normalizePipelineBoardColumn(
  value: unknown,
  index = 0,
): PipelineBoardColumn {
  const source = record(value) ?? {};
  const id = text(source.id) ?? `column-${index + 1}`;
  return {
    id,
    title: text(source.title) ?? id,
    kind: text(source.kind) ?? id,
  };
}

export function normalizePipelineBoardCard(
  value: unknown,
  index = 0,
): PipelineBoardCard {
  const source = record(value) ?? {};
  const runID = text(source.run_id);
  const issueID = text(source.issue_id);
  const id = text(source.id) ?? runID ?? issueID ?? `card-${index + 1}`;
  const entryInput = record(source.entry_input) ?? undefined;
  const attempts = normalizeAttempts(source.attempts);
  const reviews = normalizePendingReviews(source.pending_reviews);
  const external = normalizeExternal(source.external);
  const giveUp = normalizeGiveUp(source.gave_up);
  return {
    id,
    kind: text(source.kind) ?? (runID ? "run" : "task"),
    column_id: text(source.column_id) ?? "opened",
    title: text(source.title) ?? id,
    ...(text(source.body) ? { body: text(source.body) } : {}),
    ...(issueID ? { issue_id: issueID } : {}),
    ...(text(source.issue_state) ? { issue_state: text(source.issue_state) } : {}),
    ...(stringArray(source.labels) !== undefined
      ? { labels: stringArray(source.labels) }
      : {}),
    ...(numberValue(source.priority) !== undefined
      ? { priority: numberValue(source.priority) }
      : {}),
    ...(normalizeBlockers(source.blockers) !== undefined
      ? { blockers: normalizeBlockers(source.blockers) }
      : {}),
    ...(numberValue(source.open_blocker_count) !== undefined
      ? { open_blocker_count: intValue(source.open_blocker_count, 0) }
      : {}),
    ...(text(source.launch_blocked_reason)
      ? { launch_blocked_reason: text(source.launch_blocked_reason) }
      : {}),
    ...(normalizeBlocking(source.blocking) !== undefined
      ? { blocking: normalizeBlocking(source.blocking) }
      : {}),
    // Planner provenance. This normalizer is a WHITELIST — a field absent
    // here never reaches the views, however faithfully the server emits it
    // (that is exactly how the parent card lost its "N / M closed" children
    // counter and its Plan badge while /api/v1/pipeline-board was serving
    // both). Keep it in sync with PipelineBoardCard.
    ...(text(source.parent_issue_id)
      ? { parent_issue_id: text(source.parent_issue_id) }
      : {}),
    ...(text(source.parent_title)
      ? { parent_title: text(source.parent_title) }
      : {}),
    ...(normalizeChildRefs(source.children) !== undefined
      ? { children: normalizeChildRefs(source.children) }
      : {}),
    ...(normalizeChildrenSummary(source.children_summary) !== undefined
      ? { children_summary: normalizeChildrenSummary(source.children_summary) }
      : {}),
    ...(text(source.role) ? { role: text(source.role) } : {}),
    ...(runID ? { run_id: runID } : {}),
    ...(text(source.workflow_name)
      ? { workflow_name: text(source.workflow_name) }
      : {}),
    ...(text(source.bot_id) ? { bot_id: text(source.bot_id) } : {}),
    ...(text(source.status) ? { status: text(source.status) } : {}),
    ...(text(source.error) ? { error: text(source.error) } : {}),
    ...(typeof source.failed === "boolean" ? { failed: source.failed } : {}),
    ...(typeof source.reserves_slot === "boolean"
      ? { reserves_slot: source.reserves_slot }
      : {}),
    ...(giveUp ? { gave_up: giveUp } : {}),
    ...(typeof source.ready === "boolean" ? { ready: source.ready } : {}),
    ...(entryInput ? { entry_input: entryInput } : {}),
    ...(numberValue(source.queue_position) !== undefined
      ? { queue_position: numberValue(source.queue_position) }
      : {}),
    executed_nodes: intValue(source.executed_nodes, 0),
    total_nodes: intValue(source.total_nodes, 0),
    tree_executed_nodes: intValue(source.tree_executed_nodes, 0),
    tree_total_nodes: intValue(source.tree_total_nodes, 0),
    ...(numberValue(source.descendant_count) !== undefined
      ? { descendant_count: numberValue(source.descendant_count) }
      : {}),
    ...(stringArray(source.tree_run_ids) !== undefined
      ? { tree_run_ids: stringArray(source.tree_run_ids) }
      : {}),
    ...(reviews !== undefined ? { pending_reviews: reviews } : {}),
    ...(text(source.output) ? { output: text(source.output) } : {}),
    ...(external ? { external } : {}),
    ...(attempts !== undefined ? { attempts } : {}),
    created_at: text(source.created_at) ?? "",
    updated_at: text(source.updated_at) ?? "",
  };
}

function normalizeConcurrency(value: unknown): PipelineConcurrency {
  const source = record(value) ?? {};
  return {
    enabled: booleanValue(source.enabled),
    max: intValue(source.max, 0),
    active: intValue(source.active, 0),
    waiting: intValue(source.waiting, 0),
    reserved: intValue(source.reserved, 0),
  };
}

// Keep all wire-shape tolerance in this module. Views always receive one
// canonical board with the four fixed columns and folded root cards.
export function normalizePipelineBoard(value: unknown): PipelineBoard {
  const root = record(value) ?? {};
  const columns = Array.isArray(root.columns) ? root.columns : [];
  const cards = Array.isArray(root.cards) ? root.cards : [];
  return {
    columns: columns.map(normalizePipelineBoardColumn),
    cards: cards.map(normalizePipelineBoardCard),
    concurrency: normalizeConcurrency(root.concurrency),
    ...(text(root.generated_at) ? { generated_at: text(root.generated_at) } : {}),
    ...(text(root.topology_error)
      ? { topology_error: text(root.topology_error) }
      : {}),
  };
}

export async function getPipelineBoard(opts?: {
  signal?: AbortSignal;
}): Promise<PipelineBoard> {
  const raw = await apiRequest<unknown>(BASE, { signal: opts?.signal });
  return normalizePipelineBoard(raw);
}

export async function createPipelineTask(
  input: CreatePipelineTaskInput,
): Promise<NativeIssue> {
  return apiRequest<NativeIssue>(`${BASE}/tasks`, {
    method: "POST",
    body: JSON.stringify(input),
  });
}

export interface BulkReadyInput {
  ids?: string[];
  family_id?: string;
  pipeline_kind?: string;
  only_satisfied?: boolean;
}

export interface BulkReadyResult {
  ready: string[];
  waiting_deps?: string[];
  skipped?: string[];
  skipped_why?: Record<string, string>;
}

/** Stage many tickets Ready (or waiting_deps). Used by multi-select bulk. */
export async function bulkReadyPipelineTasks(
  input: BulkReadyInput,
): Promise<BulkReadyResult> {
  return apiRequest<BulkReadyResult>(`${BASE}/bulk/ready`, {
    method: "POST",
    body: JSON.stringify(input),
  });
}

export interface BulkDeleteInput {
  ids: string[];
}

export interface BulkDeleteResult {
  deleted: string[];
  skipped?: string[];
  skipped_why?: Record<string, string>;
}

/** Delete many tickets (issue only). Skips tickets with active runs. */
export async function bulkDeletePipelineTasks(
  input: BulkDeleteInput,
): Promise<BulkDeleteResult> {
  return apiRequest<BulkDeleteResult>(`${BASE}/bulk/delete`, {
    method: "POST",
    body: JSON.stringify(input),
  });
}

// markPipelineTaskReady flips a task-backed ticket between the ready and
// draft states (both stay in the Todo lane) — the write behind the board's
// Mark ready / Unmark ready / Retry buttons. The studio auto-launches ready
// tickets when a concurrency slot frees.
export async function markPipelineTaskReady(
  taskId: string,
  ready: boolean,
): Promise<void> {
  await apiRequest<unknown>(
    `${BASE}/tasks/${encodeURIComponent(taskId)}/ready`,
    {
      method: "POST",
      body: JSON.stringify({ ready }),
    },
  );
}

// deletePipelineTask removes a ticket — the Backlog card's Delete button.
// Server-guarded: refused (409) while any run in the ticket's tree is still
// active, so a live pipeline can never be detached from its ticket. Runs are
// never deleted, only the native issue.
export async function deletePipelineTask(taskId: string): Promise<void> {
  await apiRequest<unknown>(`${BASE}/tasks/${encodeURIComponent(taskId)}`, {
    method: "DELETE",
  });
}

// resetPipelineTask restarts an in-progress ticket from zero: the server
// cancels every still-active run in the ticket's tree, then restages the
// ticket to Ready so the admission loop relaunches it fresh. Refused (409)
// when a run is held by another process.
//
// `fresh` additionally drops the ticket's last-run pointer, which is what
// makes the restart deterministic — without it a live `iterion dispatch`
// resolves that pointer and RESUMES the discarded run from its checkpoint
// while the studio's own loop would have started a new one. The server
// refuses `fresh` (409) while anything in the ticket's tree is still
// non-terminal, and touches nothing when it does.
export async function resetPipelineTask(
  taskId: string,
  opts: { fresh?: boolean } = {},
): Promise<void> {
  await apiRequest<unknown>(
    `${BASE}/tasks/${encodeURIComponent(taskId)}/reset`,
    { method: "POST", body: JSON.stringify({ fresh: opts.fresh === true }) },
  );
}

// closePipelineTask ends a ticket-backed pipeline for good: the server
// cancels every still-active run in the ticket's tree, then files the
// ticket in a terminal state. The outcome is ABANDONED (`blocked`), never
// `done` — a pipeline that was closed did not deliver, and marking it done
// would auto-promote every ticket waiting on it (BlockerSatisfied counts
// only `done`) into work whose input does not exist. The caller is expected
// to have shown the operator which dependents stay parked.
//
// This is also the release valve for a needs-attention card's reserved
// concurrency slot. Refused (409) when a run is held by another process.
export async function closePipelineTask(taskId: string): Promise<void> {
  await apiRequest<unknown>(
    `${BASE}/tasks/${encodeURIComponent(taskId)}/close`,
    { method: "POST", body: JSON.stringify({}) },
  );
}

// launchPipelineTask starts a ticket immediately, jumping the admission
// loop's priority order (the Opened → In progress drag). The server still
// refuses (409) on an active run, an unfinished hard dependency, or a full
// concurrency cap — those are correctness, not ranking.
export async function launchPipelineTask(taskId: string): Promise<void> {
  await apiRequest<unknown>(
    `${BASE}/tasks/${encodeURIComponent(taskId)}/launch`,
    { method: "POST", body: JSON.stringify({}) },
  );
}

// PipelineTaskPatch is the editable surface of a not-yet-run (Backlog/ready)
// ticket. All fields optional — only sent fields are applied server-side.
export interface PipelineTaskPatch {
  title?: string;
  body?: string;
  labels?: string[];
  priority?: number;
  bot?: string;
  bot_args?: Record<string, string>;
  // Re-links the task's forge repo (absent = unchanged). Mirrors
  // NativeIssuePatch.external.
  external?: ExternalLinkInput;
  /** Replace hard-dep list wholesale; empty array clears. */
  blockers?: string[];
}

// updatePipelineTask edits a Backlog (or ready-but-unlaunched) ticket before it
// runs — title, input (bot_args), bot, description, labels, priority.
export async function updatePipelineTask(
  taskId: string,
  patch: PipelineTaskPatch,
): Promise<void> {
  await apiRequest<unknown>(`${BASE}/tasks/${encodeURIComponent(taskId)}`, {
    method: "PATCH",
    body: JSON.stringify(patch),
  });
}

// pipelineBoardImageURL builds the URL serving one workdir-relative image for
// the card sidebar's input thumbnails (e.g. a ticket's character-reference
// list). Segment-encode so subdirectories survive — encodeURIComponent on the
// whole path would clobber the "/" separators.
export function pipelineBoardImageURL(relPath: string): string {
  const segments = relPath.split("/").map(encodeURIComponent).join("/");
  return `${BASE}/workspace-images/${segments}`;
}
